import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import '../styles/nassar-theme.css';

const DateInputDDMM = ({ value, onChange, style }) => {
    const pickerRef = useRef(null);
    const [text, setText] = useState('');

    const isoToDisplay = useCallback((iso) => {
        if (!iso) return '';
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
        if (!m) return '';
        return `${m[3]}-${m[2]}-${m[1]}`;
    }, []);

    const displayToIso = useCallback((txt) => {
        const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(txt);
        if (!m) return '';
        const dd = parseInt(m[1], 10);
        const mm = parseInt(m[2], 10);
        const yyyy = parseInt(m[3], 10);
        if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yyyy < 1900 || yyyy > 2999) return '';
        return `${m[3]}-${m[2]}-${m[1]}`;
    }, []);

    useEffect(() => {
        setText(isoToDisplay(value));
    }, [value, isoToDisplay]);

    const handleTextChange = (e) => {
        const raw = e.target.value.replace(/[^\d]/g, '').slice(0, 8);
        let formatted = raw;
        if (raw.length > 4) formatted = `${raw.slice(0, 2)}-${raw.slice(2, 4)}-${raw.slice(4)}`;
        else if (raw.length > 2) formatted = `${raw.slice(0, 2)}-${raw.slice(2)}`;
        setText(formatted);
        const iso = displayToIso(formatted);
        if (iso) onChange(iso);
        else if (!formatted) onChange('');
    };

    const handleBlur = () => {
        const iso = displayToIso(text);
        if (!iso && text) setText(isoToDisplay(value));
    };

    const openPicker = () => {
        const el = pickerRef.current;
        if (!el) return;
        if (typeof el.showPicker === 'function') el.showPicker();
        else el.focus();
    };

    return (
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <input
                type="text"
                value={text}
                onChange={handleTextChange}
                onBlur={handleBlur}
                placeholder="DD-MM-YYYY"
                inputMode="numeric"
                style={{
                    padding: '0.5rem 2rem 0.5rem 0.75rem',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    outline: 'none',
                    minWidth: '140px',
                    ...style
                }}
            />
            <button
                type="button"
                onClick={openPicker}
                aria-label="open calendar"
                style={{
                    position: 'absolute',
                    right: '6px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#7a8a9e'
                }}
            >
                <svg style={{ width: '16px', height: '16px' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
            </button>
            <input
                ref={pickerRef}
                type="date"
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '1px',
                    height: '1px',
                    opacity: 0,
                    pointerEvents: 'none'
                }}
                tabIndex={-1}
            />
        </div>
    );
};

const ActivityMonitor = () => {
    const navigate = useNavigate();
    
    // Data state
    const [activities, setActivities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Users list for filter dropdown
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    
    // Filter state
    const [activeFilter, setActiveFilter] = useState('all'); // 'today', 'week', 'month', 'all'
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // Check permissions and fetch users
    useEffect(() => {
        const token = localStorage.getItem('nassar_token');
        if (token) {
            try {
                const payload = token.split('.')[1];
                const decodedPayload = JSON.parse(atob(payload));
                
                if (decodedPayload.type !== 'Admin') {
                    navigate('/dashboard/clearance-files', { replace: true });
                    return;
                }
                
                // Fetch users for filter dropdown
                fetchUsers();
            } catch (error) {
                console.error('Error decoding token:', error);
                navigate('/dashboard/clearance-files', { replace: true });
                return;
            }
        }
    }, [navigate]);

    // Fetch users list
    const fetchUsers = async () => {
        try {
            const response = await axiosInstance.get('/users/');
            setUsers(response.data);
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    };

    // Fetch activities - date filtering is handled by backend
    const fetchActivities = useCallback(async (page = 1, filter = null, customDateFrom = null, customDateTo = null, userId = null) => {
        setIsLoading(true);
        setError('');
        
        // Use passed parameters or fall back to state
        const currentFilter = filter !== null ? filter : activeFilter;
        const currentDateFrom = customDateFrom !== null ? customDateFrom : dateFrom;
        const currentDateTo = customDateTo !== null ? customDateTo : dateTo;
        const currentUserId = userId !== null ? userId : selectedUserId;
        
        try {
            let url = `/user-monitor/?page=${page}&date_filter=${currentFilter}`;
            
            // Add custom date range only when filter is 'custom'
            if (currentFilter === 'custom') {
                if (currentDateFrom) url += `&created_at_from=${currentDateFrom}`;
                if (currentDateTo) url += `&created_at_to=${currentDateTo}`;
            }
            
            // Add user filter
            if (currentUserId) {
                url += `&user_id=${currentUserId}`;
            }
            
            const response = await axiosInstance.get(url);
            
            if (response.data.results) {
                setActivities(response.data.results);
                setTotalPages(response.data.total_pages);
                setTotalCount(response.data.count);
                setCurrentPage(response.data.current_page);
            } else {
                setActivities(response.data);
                setTotalPages(1);
                setTotalCount(response.data.length);
            }
        } catch (err) {
            setError('حدث خطأ أثناء جلب البيانات');
            console.error('Error fetching activities:', err);
        } finally {
            setIsLoading(false);
        }
    }, [activeFilter, dateFrom, dateTo, selectedUserId]);

    // Initial fetch
    useEffect(() => {
        fetchActivities(1);
    }, [fetchActivities]);

    // Handle filter change
    const handleFilterChange = (filter) => {
        setActiveFilter(filter);
        if (filter !== 'custom') {
            setDateFrom('');
            setDateTo('');
        }
        setCurrentPage(1);
        // Call fetchActivities directly with the new filter
        fetchActivities(1, filter, '', '', selectedUserId);
    };

    // Handle user filter change
    const handleUserFilterChange = (userId) => {
        setSelectedUserId(userId);
        setCurrentPage(1);
        fetchActivities(1, activeFilter, dateFrom, dateTo, userId);
    };

    // Handle custom date search
    const handleCustomDateSearch = () => {
        if (dateFrom || dateTo) {
            setActiveFilter('custom');
            setCurrentPage(1);
            fetchActivities(1, 'custom', dateFrom, dateTo, selectedUserId);
        }
    };

    // Handle page change
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            fetchActivities(page, activeFilter, dateFrom, dateTo, selectedUserId);
        }
    };

    // Format date/time
    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        
        let hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const period = hours >= 12 ? 'م' : 'ص';
        hours = hours % 12 || 12;
        
        return `${day}/${month}/${year} - ${hours}:${minutes} ${period}`;
    };

    // Get action icon and color
    const getActionStyle = (actionType) => {
        if (actionType.includes('إضافة')) {
            return { color: '#10b981', bgColor: '#d1fae5', icon: '➕' };
        } else if (actionType.includes('تعديل')) {
            return { color: '#f59e0b', bgColor: '#fef3c7', icon: '✏️' };
        } else if (actionType.includes('حذف')) {
            return { color: '#ef4444', bgColor: '#fee2e2', icon: '🗑️' };
        }
        return { color: '#6b7280', bgColor: '#f3f4f6', icon: '📋' };
    };

    // Pagination component
    const Pagination = () => (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '1.5rem',
            padding: '1rem',
            flexWrap: 'wrap'
        }}>
            <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                    color: currentPage === 1 ? '#aaa' : '#03428d',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                }}
            >
                الأولى
            </button>
            <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                    color: currentPage === 1 ? '#aaa' : '#03428d',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                }}
            >
                السابق
            </button>
            
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#03428d',
                color: '#fff',
                borderRadius: '8px',
                fontWeight: '600'
            }}>
                <span>صفحة {currentPage} من {totalPages}</span>
                <span style={{ opacity: 0.8, fontSize: '0.85rem' }}>({totalCount} نتيجة)</span>
            </div>
            
            <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: currentPage === totalPages ? '#f5f5f5' : '#fff',
                    color: currentPage === totalPages ? '#aaa' : '#03428d',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                }}
            >
                التالي
            </button>
            <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: currentPage === totalPages ? '#f5f5f5' : '#fff',
                    color: currentPage === totalPages ? '#aaa' : '#03428d',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                }}
            >
                الأخيرة
            </button>
        </div>
    );

    return (
        <div className="card">
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                marginBottom: '1.5rem'
            }}>
                <h2 style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                }}>
                    <svg style={{ width: '28px', height: '28px', color: '#03428d' }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    سجل الحركات
                </h2>
            </div>

            {/* Filter Buttons */}
            <div style={{
                display: 'flex',
                gap: '0.75rem',
                marginBottom: '1rem',
                flexWrap: 'wrap',
                alignItems: 'center'
            }}>
                <button
                    onClick={() => handleFilterChange('all')}
                    style={{
                        padding: '0.6rem 1.25rem',
                        borderRadius: '10px',
                        border: 'none',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        backgroundColor: activeFilter === 'all' ? '#03428d' : '#f1f5f9',
                        color: activeFilter === 'all' ? '#fff' : '#64748b'
                    }}
                >
                    كل الحركات
                </button>
                <button
                    onClick={() => handleFilterChange('today')}
                    style={{
                        padding: '0.6rem 1.25rem',
                        borderRadius: '10px',
                        border: 'none',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        backgroundColor: activeFilter === 'today' ? '#03428d' : '#f1f5f9',
                        color: activeFilter === 'today' ? '#fff' : '#64748b'
                    }}
                >
                    اليوم
                </button>
                <button
                    onClick={() => handleFilterChange('week')}
                    style={{
                        padding: '0.6rem 1.25rem',
                        borderRadius: '10px',
                        border: 'none',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        backgroundColor: activeFilter === 'week' ? '#03428d' : '#f1f5f9',
                        color: activeFilter === 'week' ? '#fff' : '#64748b'
                    }}
                >
                    هذا الأسبوع
                </button>
                <button
                    onClick={() => handleFilterChange('month')}
                    style={{
                        padding: '0.6rem 1.25rem',
                        borderRadius: '10px',
                        border: 'none',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        backgroundColor: activeFilter === 'month' ? '#03428d' : '#f1f5f9',
                        color: activeFilter === 'month' ? '#fff' : '#64748b'
                    }}
                >
                    هذا الشهر
                </button>
                
                {/* Separator */}
                <div style={{
                    width: '1px',
                    height: '30px',
                    backgroundColor: '#e2e8f0',
                    margin: '0 0.5rem'
                }} />
                
                {/* User Filter Dropdown */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <svg style={{ width: '20px', height: '20px', color: '#64748b' }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    <select
                        value={selectedUserId}
                        onChange={(e) => handleUserFilterChange(e.target.value)}
                        style={{
                            padding: '0.6rem 1rem',
                            borderRadius: '10px',
                            border: '2px solid #e2e8f0',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            backgroundColor: selectedUserId ? '#03428d' : '#fff',
                            color: selectedUserId ? '#fff' : '#64748b',
                            outline: 'none',
                            minWidth: '160px',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <option value="" style={{ backgroundColor: '#fff', color: '#64748b' }}>كل المستخدمين</option>
                        {users.map(u => (
                            <option key={u.id} value={u.id} style={{ backgroundColor: '#fff', color: '#1e293b' }}>
                                {u.name} {u.type === 'Admin' ? '(مدير)' : ''}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Date Range Filters */}
            <div style={{
                display: 'flex',
                gap: '0.75rem',
                marginBottom: '1.5rem',
                flexWrap: 'wrap',
                alignItems: 'center'
            }}>
                {/* Custom Date Range */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    flexWrap: 'wrap',
                    direction: 'rtl'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '600' }}>من:</span>
                        <DateInputDDMM value={dateFrom} onChange={setDateFrom} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '600' }}>إلى:</span>
                        <DateInputDDMM value={dateTo} onChange={setDateTo} />
                    </div>
                    <button
                        onClick={handleCustomDateSearch}
                        disabled={!dateFrom && !dateTo}
                        style={{
                            padding: '0.5rem 1.25rem',
                            borderRadius: '8px',
                            border: 'none',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            cursor: (!dateFrom && !dateTo) ? 'not-allowed' : 'pointer',
                            backgroundColor: (!dateFrom && !dateTo) ? '#e2e8f0' : '#03428d',
                            color: (!dateFrom && !dateTo) ? '#94a3b8' : '#fff',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        بحث
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div style={{
                    backgroundColor: '#fee2e2',
                    color: '#dc2626',
                    padding: '1rem',
                    borderRadius: '10px',
                    marginBottom: '1rem',
                    textAlign: 'center',
                    fontWeight: '500'
                }}>
                    {error}
                </div>
            )}

            {/* Loading State */}
            {isLoading ? (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '3rem',
                    flexDirection: 'column',
                    gap: '1rem'
                }}>
                    <div style={{
                        width: '50px',
                        height: '50px',
                        border: '4px solid #e2e8f0',
                        borderTop: '4px solid #03428d',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }} />
                    <span style={{ color: '#64748b', fontWeight: '500' }}>جاري التحميل...</span>
                    <style>{`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}</style>
                </div>
            ) : activities.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '3rem',
                    color: '#64748b'
                }}>
                    <svg style={{ width: '64px', height: '64px', margin: '0 auto 1rem', opacity: 0.5 }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 2h10v7h-2l-1 2H8l-1-2H5V5z" clipRule="evenodd" />
                    </svg>
                    <p style={{ fontSize: '1.1rem', fontWeight: '500' }}>لا توجد حركات</p>
                </div>
            ) : (
                <>
                    {/* Activities Table */}
                    <div style={{
                        overflowX: 'auto',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0'
                    }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '0.9rem'
                        }}>
                            <thead>
                                <tr style={{
                                    backgroundColor: '#f8fafc',
                                    borderBottom: '2px solid #e2e8f0'
                                }}>
                                    <th style={{
                                        padding: '1rem',
                                        textAlign: 'right',
                                        fontWeight: '700',
                                        color: '#1e293b',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        المستخدم
                                    </th>
                                    <th style={{
                                        padding: '1rem',
                                        textAlign: 'right',
                                        fontWeight: '700',
                                        color: '#1e293b',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        نوع العملية
                                    </th>
                                    <th style={{
                                        padding: '1rem',
                                        textAlign: 'right',
                                        fontWeight: '700',
                                        color: '#1e293b',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        التفاصيل
                                    </th>
                                    <th style={{
                                        padding: '1rem',
                                        textAlign: 'right',
                                        fontWeight: '700',
                                        color: '#1e293b',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        التاريخ والوقت
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {activities.map((activity, index) => {
                                    const actionStyle = getActionStyle(activity.action_type);
                                    return (
                                        <tr
                                            key={activity.id}
                                            style={{
                                                backgroundColor: index % 2 === 0 ? '#fff' : '#f8fafc',
                                                borderBottom: '1px solid #e2e8f0',
                                                transition: 'background-color 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f9ff'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#fff' : '#f8fafc'}
                                        >
                                            <td style={{
                                                padding: '1rem',
                                                fontWeight: '600',
                                                color: '#1e293b'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem'
                                                }}>
                                                    <div style={{
                                                        width: '36px',
                                                        height: '36px',
                                                        borderRadius: '50%',
                                                        backgroundColor: '#03428d',
                                                        color: '#fff',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontWeight: '700',
                                                        fontSize: '0.85rem'
                                                    }}>
                                                        {activity.user_name?.charAt(0) || '؟'}
                                                    </div>
                                                    {activity.user_name || 'غير معروف'}
                                                </div>
                                            </td>
                                            <td style={{ padding: '1rem' }}>
                                                <span style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '0.4rem',
                                                    padding: '0.4rem 0.8rem',
                                                    borderRadius: '20px',
                                                    backgroundColor: actionStyle.bgColor,
                                                    color: actionStyle.color,
                                                    fontWeight: '600',
                                                    fontSize: '0.85rem'
                                                }}>
                                                    <span>{actionStyle.icon}</span>
                                                    {activity.action_type}
                                                </span>
                                            </td>
                                            <td style={{
                                                padding: '1rem',
                                                color: '#475569'
                                            }}>
                                                {activity.action_content}
                                            </td>
                                            <td style={{
                                                padding: '1rem',
                                                color: '#64748b',
                                                fontSize: '0.85rem',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.4rem'
                                                }}>
                                                    <svg style={{ width: '16px', height: '16px' }} fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                                    </svg>
                                                    {formatDateTime(activity.created_at)}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && <Pagination />}
                </>
            )}
        </div>
    );
};

export default ActivityMonitor;
